import { useState } from "react";
import type { FormEvent } from "react";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { Badge, Button, Card, ConfirmButton, EmptyState, ErrorState, Input, PageHeader, Spinner, Table, Td } from "../components/ui";

interface Counter {
  key: string;
  bucket: string;
  id: string;
  windowStart: number | null;
  count: number;
  expiration: number | null;
}

export default function RateLimits() {
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const { data, loading, error, reload } = useApi<{ counters: Counter[]; truncated: boolean }>("/rate-limits");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberResult, setMemberResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function lookupMembership(e: FormEvent) {
    e.preventDefault();
    setMemberResult(null);
    setActionError(null);
    try {
      const res = await api.get<{ cached: unknown }>(`/rate-limits/membership/${encodeURIComponent(memberEmail.trim())}`);
      setMemberResult(res.cached ? JSON.stringify(res.cached, null, 2) : "Not cached");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <PageHeader title="Rate limits" subtitle="Live fixed-window counters in the auth worker's KV (they expire with their window)" />
      {actionError && <ErrorState message={actionError} />}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Active counters"
            actions={
              <Button variant="secondary" size="sm" onClick={reload}>
                Refresh
              </Button>
            }
          >
            {loading ? (
              <Spinner />
            ) : error || !data ? (
              <ErrorState message={error ?? "Failed to load"} onRetry={reload} />
            ) : data.counters.length === 0 ? (
              <EmptyState message="No active rate limit counters — nobody is being throttled." />
            ) : (
              <>
                {data.truncated && (
                  <p className="mb-2 text-xs text-amber-600">Showing the first 200 counters only.</p>
                )}
                <Table headers={["Bucket", "Identifier", "Count", canWrite ? "" : null].filter((h) => h !== null)}>
                  {data.counters.map((counter) => (
                    <tr key={counter.key}>
                      <Td>
                        <Badge tone="blue">{counter.bucket}</Badge>
                      </Td>
                      <Td className="font-mono text-xs">{counter.id}</Td>
                      <Td>{counter.count}</Td>
                      {canWrite && (
                        <Td>
                          <ConfirmButton
                            label="Clear"
                            confirmLabel="Clear counter?"
                            variant="secondary"
                            disabled={busy}
                            onConfirm={() =>
                              act(() => api.delete(`/rate-limits/counters/${encodeURIComponent(counter.key)}`))
                            }
                          />
                        </Td>
                      )}
                    </tr>
                  ))}
                </Table>
                {canWrite && data.counters.length > 0 && (
                  <p className="mt-3 text-xs text-slate-500">
                    Clearing a counter immediately unblocks that identifier for its bucket.
                  </p>
                )}
              </>
            )}
          </Card>
        </div>

        <Card title="Membership cache">
          <form onSubmit={lookupMembership} className="space-y-2">
            <Input
              type="email"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              placeholder="user@example.com"
            />
            <div className="flex gap-2">
              <Button type="submit" variant="secondary" size="sm" disabled={!memberEmail}>
                Look up
              </Button>
              {canWrite && (
                <ConfirmButton
                  label="Invalidate"
                  confirmLabel="Invalidate cache?"
                  variant="secondary"
                  disabled={busy || !memberEmail}
                  onConfirm={() =>
                    act(async () => {
                      await api.delete(`/rate-limits/membership/${encodeURIComponent(memberEmail.trim())}`);
                      setMemberResult("Invalidated");
                    })
                  }
                />
              )}
            </div>
          </form>
          {memberResult && (
            <pre className="mt-3 overflow-x-auto rounded bg-slate-50 p-2 text-xs text-slate-700">{memberResult}</pre>
          )}
          <p className="mt-3 text-xs text-slate-500">
            The auth worker caches org-membership lookups for 5 minutes. Invalidate after changing a
            user's membership on the paid platform so their next login sees it immediately.
          </p>
        </Card>
      </div>
    </div>
  );
}
